import { faCheck, faEquals, faExclamationTriangle, faWarning } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { formatQuantityToLocale } from "../../../../../utils/method";
import "./ExpandablePositionsTable.scss";

/**
 * V2 Reusable footer component for displaying quantity information in aufmass modals
 */
const QuantityDisplayFooterV2 = ({
    selectedPosition,
    totalQuantity,
    itemQuantity,
    itemLabel,
    showMismatchBadge = false,
    unitTag = "",
    positionsData = {},
}) => {
    const { t } = useTranslation();

    // Get the unit tag from either prop or position
    const displayUnitTag = unitTag || selectedPosition?.unit || selectedPosition?.unitTag || "";

    // Get the invoice quantity for comparison
    const invoiceQuantity = positionsData[selectedPosition?.id]?.invoice?.quantity || selectedPosition?.quantity || 0;

    // Computed quantity match status
    const quantityMatchStatus = (expectedQuantity, previewTotal) => {
        if (!expectedQuantity) return "insufficient";

        const parsedExpectedQuantity = parseFloat(expectedQuantity);

        if (Math.abs(previewTotal - parsedExpectedQuantity) < 0.001) {
            return "matched";
        } else if (previewTotal > parsedExpectedQuantity) {
            return "exceeded";
        } else {
            return "insufficient";
        }
    };

    // Helper function to determine badge color based on quantity status
    const getQuantityBadgeColor = (status) => {
        switch (status) {
            case "matched":
                return "success";
            case "exceeded":
                return "warning";
            case "insufficient":
                return "danger";
            default:
                return "secondary";
        }
    };

    // Memoize the current status and badge color to ensure they update with prop changes
    const currentStatus = useMemo(() => {
        return quantityMatchStatus(invoiceQuantity, totalQuantity);
    }, [invoiceQuantity, totalQuantity]);

    const currentBadgeColor = useMemo(() => {
        return getQuantityBadgeColor(currentStatus);
    }, [currentStatus]);

    // Helper function for status icons
    const getStatusIcon = (status) => {
        const statusConfig = {
            matched: {
                icon: faCheck,
                tooltipKey: "aufmass.quantity-status.matched-tooltip",
                className: "status-matched",
            },
            exceeded: {
                icon: faExclamationTriangle,
                tooltipKey: "aufmass.quantity-status.exceeded-tooltip",
                className: "status-exceeded",
            },
            insufficient: {
                icon: faWarning,
                tooltipKey: "aufmass.quantity-status.insufficient-tooltip",
                className: "status-insufficient",
            },
        };

        if (!statusConfig[status]) return null;

        const config = statusConfig[status];
        const tooltipContent = t(config.tooltipKey);

        return (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip>{tooltipContent}</Tooltip>}
            >
                <FontAwesomeIcon
                    icon={config.icon}
                    className={`status-icon ${config.className}`}
                />
            </OverlayTrigger>
        );
    };

    return (
        <div className="d-flex justify-content-between w-100 align-items-center">
            {/* Left side: Invoice, LV quantities */}
            <div className="d-flex align-items-center gap-4">
                <div className="lv-quantity">
                    <span className="quantity-label me-1">
                        {t("aufmass.lv-quantity")}:
                    </span>
                    <Badge bg="info" className="px-2 py-1">
                        {`${formatQuantityToLocale(selectedPosition?.lvQuantity || selectedPosition?.quantity || 0)} ${displayUnitTag}`}
                    </Badge>
                </div>
                <div className="invoice-quantity">
                    <span className="quantity-label me-1">
                        {t("invoice.quantity")}:
                    </span>
                    <Badge bg="secondary" className="px-2 py-1">
                        {`${formatQuantityToLocale(invoiceQuantity)} ${displayUnitTag}`}
                    </Badge>
                </div>

                <div className="aufmass-quantity">
                    <span className="quantity-label me-1">
                        {t("aufmass.quantity")}:
                    </span>
                    <Badge
                        bg={currentBadgeColor}
                        className="px-2 py-1"
                    >
                        {`${formatQuantityToLocale(totalQuantity)} ${displayUnitTag}`}
                    </Badge>
                    {getStatusIcon(currentStatus)}
                </div>
            </div>

            {/* Right side: Line/Group quantity */}
            <div className="d-flex align-items-center">
                <span className="me-2 am-text-sm">
                    <FontAwesomeIcon icon={faEquals} className="me-2" />
                    {itemLabel}:
                </span>
                <Badge
                    bg="primary"
                    className="py-2 px-3 am-badge-md"
                >
                    {`${formatQuantityToLocale(itemQuantity)} ${displayUnitTag}`}
                </Badge>
                {showMismatchBadge && (
                    <Badge bg="warning" className="ms-2">
                        {t("aufmass.quantity-mismatch")}
                    </Badge>
                )}
            </div>
        </div>
    );
};

export default QuantityDisplayFooterV2;
